// SCHEDULE_JSON 파싱·표시·장바구니 진입 판정

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토']

function padHHmm (value) {
  const digits = String(value == null ? '' : value).replace(/\D/g, '')
  return digits.padStart(4, '0').slice(0, 4)
}

function isAllDay (start, end) {
  const s = padHHmm(start)
  const e = padHHmm(end)
  return s === e
}

export function formatHHmm (hhmm) {
  const v = padHHmm(hhmm)
  return `${v.slice(0, 2)}:${v.slice(2, 4)}`
}

export function formatRange (range) {
  if (!range || range.start == null || range.end == null) {
    return ''
  }
  if (isAllDay(range.start, range.end)) {
    return '24시간'
  }
  return `${formatHHmm(range.start)} ~ ${formatHHmm(range.end)}`
}

export function formatRangeCompact (range) {
  if (!range || range.start == null || range.end == null) {
    return ''
  }
  if (isAllDay(range.start, range.end)) {
    return '24시간'
  }
  return `${formatHHmm(range.start)}~${formatHHmm(range.end)}`
}

export { DAY_NAMES }

export function formatSlot (slot) {
  if (!slot) {
    return ''
  }
  if (isAllDay(slot.start, slot.end)) {
    return '24시간 배달'
  }
  const deadline = slot.deadline != null ? formatHHmm(slot.deadline) : ''
  const base = `${formatHHmm(slot.start)} ~ ${formatHHmm(slot.end)}`
  return deadline ? `${base} (마감 ${deadline})` : base
}

export function parseScheduleJson (raw) {
  let value = raw
  if (value == null || value === '') {
    return null
  }
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch (e) {
      return null
    }
  }
  if (typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length === 0) {
    return null
  }
  if (!value.mode || !value.order || !value.delivery) {
    return null
  }
  return value
}

/** storeInfo / storeDeliveryInfo 에서 스케줄 추출 */
export function getScheduleFromStore (storeInfo, storeDeliveryInfo) {
  const candidates = [
    storeInfo && storeInfo.SCHEDULE_JSON,
    storeInfo && storeInfo.scheduleJson,
    storeDeliveryInfo && storeDeliveryInfo.SCHEDULE_JSON,
    storeDeliveryInfo && storeDeliveryInfo.scheduleJson,
    storeDeliveryInfo && storeDeliveryInfo.result && storeDeliveryInfo.result.scheduleJson,
    storeDeliveryInfo && storeDeliveryInfo.result && storeDeliveryInfo.result.SCHEDULE_JSON
  ]
  for (let i = 0; i < candidates.length; i++) {
    const parsed = parseScheduleJson(candidates[i])
    if (parsed) {
      return parsed
    }
  }
  return null
}

function isWeekend (dayOfWeek) {
  return dayOfWeek === 0 || dayOfWeek === 6
}

export function getTodayOrderRange (schedule, dayOfWeek) {
  if (!schedule || !schedule.order) {
    return null
  }
  const mode = schedule.mode || 'everyday'
  if (mode === 'everyday') {
    return schedule.order.everyday || null
  }
  if (mode === 'weekdayWeekend') {
    return isWeekend(dayOfWeek) ? (schedule.order.weekend || null) : (schedule.order.weekday || null)
  }
  if (mode === 'byDay' && Array.isArray(schedule.order.byDay)) {
    for (let i = 0; i < schedule.order.byDay.length; i++) {
      const group = schedule.order.byDay[i]
      if (group && Array.isArray(group.dayOfWeek) && group.dayOfWeek.map(Number).includes(Number(dayOfWeek))) {
        return { start: group.start, end: group.end }
      }
    }
  }
  return null
}

export function getTodayDeliverySlots (schedule, dayOfWeek) {
  if (!schedule || !schedule.delivery) {
    return []
  }
  const mode = schedule.mode || 'everyday'
  if (mode === 'everyday') {
    return Array.isArray(schedule.delivery.everyday) ? schedule.delivery.everyday : []
  }
  if (mode === 'weekdayWeekend') {
    const slots = isWeekend(dayOfWeek) ? schedule.delivery.weekend : schedule.delivery.weekday
    return Array.isArray(slots) ? slots : []
  }
  if (mode === 'byDay' && Array.isArray(schedule.delivery.byDay)) {
    for (let i = 0; i < schedule.delivery.byDay.length; i++) {
      const group = schedule.delivery.byDay[i]
      if (group && Array.isArray(group.dayOfWeek) && group.dayOfWeek.map(Number).includes(Number(dayOfWeek))) {
        return Array.isArray(group.slots) ? group.slots : []
      }
    }
  }
  return []
}

function isInRange (now, start, end) {
  const n = Number(padHHmm(now))
  const s = Number(padHHmm(start))
  const e = Number(padHHmm(end))
  if (s === e) {
    return true
  }
  if (s < e) {
    return s <= n && n <= e
  }
  return n >= s || n <= e
}

function isSlotActive (now, slot) {
  if (!slot) {
    return false
  }
  if (isAllDay(slot.start, slot.end)) {
    return true
  }
  if (!isInRange(now, slot.start, slot.end)) {
    return false
  }
  const n = Number(padHHmm(now))
  const deadline = Number(padHHmm(slot.deadline != null ? slot.deadline : slot.end))
  return n <= deadline
}

function sortSlots (slots) {
  return (slots || []).slice().sort((a, b) => Number(padHHmm(a.start)) - Number(padHHmm(b.start)))
}

function isSlotOpen (now, slot) {
  if (!slot) {
    return false
  }
  if (isAllDay(slot.start, slot.end)) {
    return true
  }
  return Number(padHHmm(now)) <= Number(padHHmm(slot.deadline != null ? slot.deadline : slot.end))
}

function remainingSlots (slots, now) {
  return sortSlots(slots).filter((slot) => isSlotOpen(now, slot))
}

function dayLabels (days) {
  if (!Array.isArray(days) || days.length === 0) {
    return ''
  }
  return days.map((d) => DAY_NAMES[Number(d)] || d).join(', ')
}

/** 매장정보 표시용 줄 목록 */
export function buildScheduleDisplay (schedule) {
  const orderLines = []
  const deliveryLines = []
  if (!schedule) {
    return { orderLines, deliveryLines }
  }
  const mode = schedule.mode || 'everyday'

  if (mode === 'everyday') {
    orderLines.push(formatRange(schedule.order.everyday))
    ;(schedule.delivery.everyday || []).forEach((slot) => {
      deliveryLines.push(formatSlot(slot))
    })
  } else if (mode === 'weekdayWeekend') {
    orderLines.push(`주중 : ${formatRange(schedule.order.weekday)}`)
    orderLines.push(`주말 : ${formatRange(schedule.order.weekend)}`)
    const weekdaySlots = schedule.delivery.weekday || []
    const weekendSlots = schedule.delivery.weekend || []
    if (weekdaySlots.length) {
      deliveryLines.push(`주중 : ${weekdaySlots.map(formatSlot).join(', ')}`)
    }
    if (weekendSlots.length) {
      deliveryLines.push(`주말 : ${weekendSlots.map(formatSlot).join(', ')}`)
    }
  } else if (mode === 'byDay') {
    const orderByDay = schedule.order.byDay || []
    const deliveryByDay = schedule.delivery.byDay || []
    orderByDay.forEach((group) => {
      orderLines.push(`${dayLabels(group.dayOfWeek)} : ${formatRange(group)}`)
    })
    deliveryByDay.forEach((group) => {
      const slots = (group.slots || []).map(formatSlot).join(', ')
      deliveryLines.push(`${dayLabels(group.dayOfWeek)} : ${slots}`)
    })
  }

  return {
    orderLines: orderLines.filter(Boolean),
    deliveryLines: deliveryLines.filter(Boolean)
  }
}

function buildSlotNotice (slot) {
  if (!slot) {
    return { deliveryText: '', deadlineText: '' }
  }
  return {
    deliveryText: formatRangeCompact(slot),
    deadlineText: isAllDay(slot.start, slot.end) ? '' : formatHHmm(slot.deadline != null ? slot.deadline : slot.end)
  }
}

/**
 * 장바구니 진입 판정
 * @returns {{ status: 'ok'|'nextSlot'|'nextDay'|'orderClosed' }}
 */
export function evaluateCartAccess (schedule, nowHHmm, dayOfWeek) {
  const order = getTodayOrderRange(schedule, dayOfWeek)
  const slots = getTodayDeliverySlots(schedule, dayOfWeek)
  const orderText = formatRange(order) || '-'
  const deliveryText = slots.length
    ? slots.map((s) => formatRangeCompact(s) || '24시간').join(', ')
    : '-'

  const remaining = remainingSlots(slots, nowHHmm)
  const hasActive = slots.some((slot) => isSlotActive(nowHHmm, slot))
  if (hasActive) {
    return { status: 'ok', orderText, deliveryText }
  }

  if (remaining.length > 0) {
    const slotNotice = buildSlotNotice(remaining[0])
    return {
      status: 'nextSlot',
      orderText,
      deliveryText: slotNotice.deliveryText,
      deadlineText: slotNotice.deadlineText
    }
  }

  let nextDay = null
  let daysAhead = 0
  for (let i = 1; i <= 7; i++) {
    const d = (Number(dayOfWeek) + i) % 7
    const daySlots = getTodayDeliverySlots(schedule, d)
    if (daySlots.length > 0) {
      nextDay = d
      daysAhead = i
      break
    }
  }

  if (nextDay == null) {
    return { status: 'orderClosed', orderText, deliveryText }
  }

  const nextOrder = getTodayOrderRange(schedule, nextDay)
  const nextSlots = sortSlots(getTodayDeliverySlots(schedule, nextDay))
  const slotNotice = buildSlotNotice(nextSlots[0])
  const tomorrowName = DAY_NAMES[(Number(dayOfWeek) + 1) % 7]

  return {
    status: 'nextDay',
    orderText: formatRangeCompact(nextOrder),
    deliveryText: slotNotice.deliveryText,
    deadlineText: slotNotice.deadlineText,
    todayName: DAY_NAMES[Number(dayOfWeek)],
    nextDayName: DAY_NAMES[nextDay],
    tomorrowName,
    showTomorrowHoliday: daysAhead > 1
  }
}

export function getDayOfWeekNumber (dayjsInstance) {
  // dayjs: 0=일 … 6=토 (스케줄 JSON과 동일)
  return dayjsInstance.day()
}



// WEBPACK FOOTER //
// ./src/js/scheduleJson.js